'use client'

import { ApolloProvider } from '@apollo/client/react'
import { ScrollToTop } from '@utils/ScrollToTop'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import { PostHogProvider } from 'posthog-js/react'
import { type ReactNode, useEffect, useState } from 'react'
import { CookiesProvider } from 'react-cookie'
import { I18nextProvider } from 'react-i18next'

import { client } from '@/apollo/main/client'
import { AuthProvider } from '@/auth/AuthProvider'
import { DevProfiler } from '@/components/molecules/DevProfiler'
import { ColorModeProvider } from '@/components/ui/color-mode'
import {
  BrandingProvider,
  type NetworkBranding,
} from '@/contexts/BrandingContext'
import { Provider as ChakraProvider } from '@/components/ui/provider'
import { env } from '@/config/env'
import { DevProfilerProvider } from '@/contexts/DevProfilerContext'
import { SwitchContextProvider } from '@/contexts/SwitchContextContext'
import i18n from '@/translations'

import { AppSkeleton } from './_lib/AppSkeleton'
import { HtmlLangSync } from './_lib/HtmlLangSync'
import { PostHogPageview } from './_lib/PostHogPageview'
import { WebVitals } from './_lib/WebVitals'

/**
 * Client provider tree for the Next.js App Router — mirrors `src/app/RootShell.tsx`
 * plus the outer `CookiesProvider`/`PostHogProvider` from `src/index.tsx`.
 *
 * Client-only mount gate: the providers (and the whole app) read browser APIs at
 * init time — `DevProfilerProvider` reads `document.cookie`, OIDC/Apollo read
 * `localStorage`, `ScrollToTop`/`AuthProvider` use routing hooks. During the
 * lift-and-shift phase the app stays a pure SPA: we render a static `AppSkeleton`
 * on the server and mount the real tree on the client. The first client render
 * matches the server (both the skeleton), so there is no hydration mismatch.
 * This is the minimal `ClientOnly` boundary;
 * per-page `dynamic(..., { ssr: false })` and real SSR come later (It. 2 / It. 9).
 *
 * It. 1 completes the tree: `AuthProvider`/`ScrollToTop`/`DevProfiler` now import
 * routing/env via `@/routing` + `@/config/env`, which resolve to `next/navigation`
 * and `process.env.NEXT_PUBLIC_*` under Next.
 */
const posthogKey = env.PUBLIC_POSTHOG_KEY?.trim() ?? ''
const posthogHost = env.PUBLIC_POSTHOG_HOST?.trim() ?? ''
const isPostHogEnabled = Boolean(posthogKey && posthogHost)

/**
 * `ColorModeProvider` (next-themes) is hoisted ABOVE the client-only mount gate
 * so it renders on the server: its anti-flash `<script>` lands in the SSR HTML and
 * hydrates normally. Rendered only on the client (inside the gate), the script is
 * created during a client render, which React 19 never executes → console warning.
 * The `ColorModeProvider` nested inside Chakra (`provider.tsx`) detects this parent
 * via context and becomes a no-op passthrough, so there is no duplicate script.
 */
export const Providers = ({
  branding,
  children,
}: {
  branding: NetworkBranding | null
  children: ReactNode
}) => {
  return (
    // Above the client-only mount gate: the branding is already resolved on the server, so
    // components can read it on their very first render instead of waiting for an effect.
    <BrandingProvider branding={branding}>
      <ColorModeProvider>
        <ClientProviders>{children}</ClientProviders>
      </ColorModeProvider>
    </BrandingProvider>
  )
}

const ClientProviders = ({ children }: { children: ReactNode }) => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  // Server render + first client render (mounted === false) paint the static
  // shell so the page is never blank before hydration. Both render the same
  // markup, so there is no hydration mismatch. Replaced once the client mounts.
  if (!mounted) return <AppSkeleton />

  const tree = (
    <ChakraProvider>
      <ApolloProvider client={client}>
        <SwitchContextProvider>
          <AuthProvider>
            <I18nextProvider i18n={i18n}>
              <HtmlLangSync />
              <DevProfilerProvider>
                <ScrollToTop />
                <DevProfiler />
                <PostHogPageview />
                <WebVitals />
                {children}
              </DevProfilerProvider>
            </I18nextProvider>
          </AuthProvider>
        </SwitchContextProvider>
      </ApolloProvider>
    </ChakraProvider>
  )

  return (
    <NuqsAdapter>
      <CookiesProvider>
        {isPostHogEnabled ? (
          <PostHogProvider
            apiKey={posthogKey}
            options={{ api_host: posthogHost }}
          >
            {tree}
          </PostHogProvider>
        ) : (
          tree
        )}
      </CookiesProvider>
    </NuqsAdapter>
  )
}
